import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';

export interface PendingMessage {
  id: string;
  family_name: string;
  student_name: string;
  message_type: 'inquiry' | 'concern' | 'request';
  days_pending: number;
  priority: 'high' | 'medium' | 'low';
}

export interface MessageResponseData {
  pending_messages: PendingMessage[];
  count: number;
}

export const useMessageResponseUrgency = () => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('useMessageResponseUrgency');
  
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['messageResponseUrgency', organizationId] as const,
    queryFn: async (): Promise<MessageResponseData> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchMessageResponseUrgency',
          { userId: user?.id },
          false
        );
      }

      try {
        // For now, return empty data as we don't have a messages table implemented yet
        // TODO: Implement when communication/messaging system is added
        // This would query unread messages from families waiting for responses
        
        return {
          pending_messages: [],
          count: 0,
        };
      } catch (error) {
        throw errorHandler.handleError(
          error,
          'fetchMessageResponseUrgency',
          { 
            userId: user?.id,
            organizationId,
            category: ErrorCategory.DATABASE
          },
          false
        );
      }
    },
    staleTime: 2 * 60 * 1000, // Very fresh - 2 minutes
    gcTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: true, // Important for responsiveness
    enabled: !!organizationId,
  });
};